import { css } from '@emotion/react';
import { clamp } from '@polestar/component-warehouse-react/helpers/mathematical';
import styled from '@emotion/styled';
import useWindowSize from '@polestar/component-warehouse-react/hooks/useWindowSize';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import React, { forwardRef, useCallback, useEffect, useRef, useState, } from 'react';
import ScrollIndicator from '@polestar/component-warehouse-react/components/scroll-indicator/ScrollIndicator';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { m as motion } from 'framer-motion';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import useDimensionsCallback, { useWidthCallback, } from '@polestar/component-warehouse-react/hooks/useDimensions';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import useDirectionStyle from '@polestar/component-warehouse-react/hooks/useDirectionStyle';
import { shouldForwardValidProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { useDrawerTranslateOffset } from '@polestar/component-warehouse-react/components/drawer/DrawerContext';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
var Cursor;
(function (Cursor) {
    Cursor["grab"] = "grab";
    Cursor["grabbing"] = "grabbing";
})(Cursor || (Cursor = {}));
var HorizontalScrollViewCSSCustomProps;
(function (HorizontalScrollViewCSSCustomProps) {
    HorizontalScrollViewCSSCustomProps["preferredMarginBottom"] = "--preferred-margin-bottom";
})(HorizontalScrollViewCSSCustomProps || (HorizontalScrollViewCSSCustomProps = {}));
/**
 * This value is needed to counteract the appearance of the scrollbar on mobile
 * It makes the scrollbar invisible with a negative margin and padding trick
 * It also needed to subtract from a spacing value
 */
const scrollBarDimension = 15;
const HorizontalScrollView = forwardRef(({ children, onScroll, monochrome, reduceVerticalGap = false, ...props }, ref) => {
    const memoizedId = useSSRSafeId('horizontal-scrollview', props.id);
    const memoizedContainerId = `${memoizedId}-container`;
    const memoizedScrollIndicatorId = `${memoizedId}-scroll-indicator`;
    const [mockDrag, setMockDrag] = useState(true);
    const [showIndicator, setShowIndicator] = useState(true);
    const { bodyWidth } = useWindowSize();
    const { isRTL } = useDirection();
    const originalContainerRef = useRef(null);
    const internalRef = useRef(null);
    const combinedRef = useCombinedRef(ref, internalRef);
    const [mouseDown, setMouseDown] = useState(false);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [dragging, setDragging] = useState(false);
    const [scrollContentRef, scrollContentWidth] = useWidthCallback('offsetWidth');
    const renderedWidth = scrollContentWidth === 0 ? bodyWidth : scrollContentWidth;
    const initialized = renderedWidth > 0;
    const [innerRef, dimensions] = useDimensionsCallback();
    const { translateOffsetLeft } = useDrawerTranslateOffset();
    const leftOffset = Math.max(0, dimensions.left + translateOffsetLeft || 0);
    const rightOffset = Math.max(0, bodyWidth - (dimensions.width + leftOffset) || 0);
    const maxScrollLeft = useDirectionStyle(renderedWidth + (rightOffset + leftOffset) - bodyWidth);
    // Determine if we need the indicator at the bottom
    useEffect(() => {
        const needsIndicator = renderedWidth > dimensions.width;
        setShowIndicator(needsIndicator);
    }, [renderedWidth, dimensions.width]);
    // Update our local scroll position based on native scrolling
    const handleScroll = useCallback((e) => {
        if (onScroll) {
            onScroll(e);
        }
        if (!mouseDown && (internalRef === null || internalRef === void 0 ? void 0 : internalRef.current)) {
            const clampedScroll = clamp(internalRef.current.scrollLeft, isRTL ? maxScrollLeft : 0, isRTL ? 0 : maxScrollLeft);
            setScrollLeft(clampedScroll);
        }
    }, [mouseDown, maxScrollLeft, onScroll, isRTL]);
    const offsetRef = useRef(0);
    const handlePan = useCallback((event, info) => {
        if (!mockDrag) {
            return;
        }
        const newScrollLeft = Math[isRTL ? 'min' : 'max'](0, Math.min(renderedWidth - dimensions.width, info.offset.x * -1 + offsetRef.current));
        if (internalRef.current) {
            internalRef.current.scrollLeft = newScrollLeft;
        }
        setScrollLeft(newScrollLeft);
        setDragging(true);
    }, [dimensions.width, mockDrag, renderedWidth, isRTL]);
    const handlePanStart = useCallback(() => {
        if (internalRef.current) {
            offsetRef.current = internalRef.current.scrollLeft + window.scrollX;
        }
        setMouseDown(true);
        setDragging(false);
    }, []);
    const handlePanEnd = useCallback(() => {
        setMouseDown(false);
        setDragging(false);
    }, []);
    const handleTouchStart = useCallback(() => {
        setMockDrag(false);
    }, []);
    const progress = (100 * scrollLeft) / maxScrollLeft;
    const handleProgress = useCallback((newProgress) => {
        const newScrollLeft = (newProgress / 100) * maxScrollLeft;
        setScrollLeft(newScrollLeft);
        if (internalRef === null || internalRef === void 0 ? void 0 : internalRef.current) {
            internalRef.current.scrollLeft = newScrollLeft;
        }
    }, [maxScrollLeft]);
    return (React.createElement("div", { ...props, ref: ref },
        React.createElement("div", { ref: innerRef }),
        React.createElement(StyledInner, { onPan: handlePan, onMouseDown: handlePanStart, onMouseUp: handlePanEnd, onPanEnd: handlePanEnd, onTouchStart: handleTouchStart, reduceVerticalGap: reduceVerticalGap },
            initialized ? null : (React.createElement(StyledContainer, { id: memoizedContainerId, ref: originalContainerRef, reference: true, initialized: initialized }, children)),
            React.createElement(StyledContainer, { ref: combinedRef, onScroll: handleScroll, initialized: initialized, leftOffset: leftOffset, rightOffset: rightOffset, width: dimensions.width, cursor: showIndicator ? (mouseDown ? Cursor.grabbing : Cursor.grab) : null, tabIndex: 0, role: "tabpanel" // Needed because https://dequeuniversity.com/rules/axe/4.4/focus-order-semantics
                , "aria-owns": showIndicator ? memoizedScrollIndicatorId : null, id: memoizedContainerId },
                React.createElement(Spacer, { style: {
                        minWidth: leftOffset,
                        maxWidth: leftOffset,
                    } }),
                React.createElement(Content, { dragging: dragging, ref: scrollContentRef }, children),
                React.createElement(Spacer, { style: {
                        minWidth: rightOffset,
                        maxWidth: rightOffset,
                    } }))),
        showIndicator && (React.createElement(ScrollIndicator, { handleSize: "auto", orientation: Orientations.horizontal, scrollContentSize: renderedWidth, progress: progress, onProgress: handleProgress, animated: false, id: memoizedScrollIndicatorId, monochrome: monochrome, ...aria({
                controls: memoizedContainerId,
            }) }))));
});
HorizontalScrollView.displayName = 'HorizontalScrollView';
const StyledInner = styled(motion.div)(({ reduceVerticalGap }) => css `
    ${HorizontalScrollViewCSSCustomProps.preferredMarginBottom}: ${Spacings.xLarge};
    inline-size: 100%;
    position: relative;
    margin-bottom: calc(
      var(${HorizontalScrollViewCSSCustomProps.preferredMarginBottom}) *
        ${!!reduceVerticalGap ? 0.5 : 1} - ${scrollBarDimension}px
    );

    ${MediaQueries.tablet} {
      ${HorizontalScrollViewCSSCustomProps.preferredMarginBottom}: ${Spacings.xxLarge};
    }

    ${MediaQueries.desktop} {
      ${HorizontalScrollViewCSSCustomProps.preferredMarginBottom}: ${Spacings.xxxLarge};
    }
  `);
const Spacer = styled.div `
  flex: 1;
  flex-shrink: 0;
  flex-grow: 0;
  min-height: 2px;
`;
const Content = styled('div', shouldForwardValidProps)(({ dragging }) => css `
    display: flex;
    flex-shrink: 0;
    pointer-events: ${dragging ? 'none' : 'auto'};
    flex-grow: 1;
  `);
const StyledContainer = styled('div', shouldForwardValidProps)(({ reference, initialized, leftOffset = 0, rightOffset = 0, cursor, width }) => css `
    inset-block-start: 0;
    inset-inline-start: -${leftOffset}px;
    inset-inline-end: -${rightOffset}px;
    cursor: ${cursor || 'default'};
    display: flex;
    flex-direction: row;

    /* hide the scrollbar on ios */
    padding: ${scrollBarDimension}px 0;
    margin: -${scrollBarDimension}px 0;

    scrollbar-width: none;
    overflow-style: none;
    -ms-overflow-style: none;
    user-select: none;

    ${reference
    ? css `
          visibility: ${initialized ? 'hidden' : 'visible'};
          overflow: hidden;
        `
    : css `
          overflow: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          position: ${initialized ? 'relative' : 'absolute'};
          width: ${width ? `${width + leftOffset + rightOffset}px` : 'auto'};
          visibility: ${initialized ? 'visible' : 'hidden'};
        `}
    &::-webkit-scrollbar {
      width: 0;
      background: transparent;
      height: 0;
      display: none;
    }
  `);
export default HorizontalScrollView;
